/**
 * @file DesktopSettingsPage.tsx
 * @brief The desktop app's Settings window (⌘,).
 *
 * @details
 * DES 02-10-2026: "koi setting too, jaisay kon sa mic use karna hai etc".
 * Loaded by DESKTOP/src/settings.js into its own window, outside every layout
 * and guard: nothing here touches our API. The settings live on the machine
 * (settings.json) and every change saves at once, as macOS settings do.
 *
 * - Recording: the microphone Boardroom and the Interpreter start with (the
 *   same list, with the same ids, as their own pickers), and whether "Include
 *   call audio" starts on.
 * - During a session: the floating widget, and the Ctrl+Option+V shortcut.
 * - App: open at login, update automatically, the window's size with a way
 *   back to the default (03-10-2026), the version.
 *
 * In a browser tab (no desktop app) it only says where Settings live.
 *
 * @author DES
 * @date 02-10-2026
 */

import { useEffect, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/Button";
import { Switch } from "@/components/ui/Switch";
import { FormFieldGroup } from "@/components/ui/FormDialog";
import { MicPicker, describeMicInUse } from "@/features/employee-boardroom/MicPicker";
import { useMicDevices } from "@/features/employee-boardroom/capture/useMicDevices";
import { canCaptureSystemAudio } from "@/features/employee-boardroom/capture/systemAudio";
import { desktopShell, type DesktopSettings } from "@/shared/desktop/desktopShell";

type AppInfo = { version: string; packaged: boolean; loginItems: boolean };

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-[13px] font-medium text-muted-foreground">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function SettingSwitch({
  title,
  detail,
  checked,
  onChange,
}: {
  title: string;
  detail: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center justify-between gap-3 border-b border-app-text/10 pb-2.5">
      <span className="min-w-0">
        <span className="block text-[15px] font-medium">{title}</span>
        <span className="block text-xs text-muted-foreground">{detail}</span>
      </span>
      <Switch checked={checked} onCheckedChange={onChange} aria-label={title} />
    </label>
  );
}

export function DesktopSettingsPage() {
  const shell = desktopShell();
  const [settings, setSettings] = useState<DesktopSettings | null>(null);
  const [info, setInfo] = useState<AppInfo | null>(null);
  const [windowSize, setWindowSize] = useState<{ width: number; height: number } | null>(null);
  const { devices, error: micError, loading: micsLoading } = useMicDevices({ hideVerblizrMic: true });
  const mac = shell?.platform === "darwin";

  useEffect(() => {
    if (!shell?.getSettings) return;
    void shell.getSettings().then((s) => s && setSettings(s));
    void shell.getAppInfo?.().then((i) => i && setInfo(i));
    // The main window may be resized while Settings is open: re-read on focus.
    const readSize = () => void shell.getWindowSize?.().then((w) => w && setWindowSize(w));
    readSize();
    window.addEventListener("focus", readSize);
    const offSettings = shell.onSettings?.((s) => setSettings(s));
    return () => {
      window.removeEventListener("focus", readSize);
      offSettings?.();
    };
  }, [shell]);

  useEffect(() => {
    document.title = "Settings";
  }, []);

  const update = (patch: Partial<DesktopSettings>) => {
    if (!settings || !shell?.setSettings) return;
    setSettings({ ...settings, ...patch });
    void shell.setSettings(patch).then(setSettings);
  };

  if (!shell?.getSettings) {
    return (
      <main className="grid min-h-screen place-items-center bg-background p-6 text-center text-sm text-muted-foreground">
        These settings are in the VERBLIZR APP for Mac and Windows.
      </main>
    );
  }

  // Shown while the app answers (a few ms): nothing to flash.
  if (!settings) return <main className="min-h-screen bg-background" />;

  // The saved mic on this page's list (ids can change when it is re-plugged).
  const micValue =
    settings.micDeviceId &&
    (devices.find((d) => d.deviceId === settings.micDeviceId) ??
      devices.find((d) => d.label === settings.micLabel))?.deviceId;
  const savedMicMissing = !!settings.micDeviceId && !micsLoading && !micValue;

  return (
    <main className="min-h-screen bg-background px-6 pb-8 pt-5 text-foreground">
      <div className="mx-auto max-w-md space-y-7">
        <Section title="Recording">
          <FormFieldGroup
            label="Microphone"
            hint={
              savedMicMissing
                ? `${settings.micLabel || "Your microphone"} is not connected. Using the system default.`
                : describeMicInUse(devices, micValue || "", micsLoading)
            }
          >
            <MicPicker
              compact
              devices={devices}
              value={micValue || ""}
              onChange={(id) =>
                update({
                  micDeviceId: id,
                  micLabel: devices.find((d) => d.deviceId === id)?.label ?? "",
                })
              }
              loading={micsLoading}
              error={null}
            />
          </FormFieldGroup>
          {micError ? (
            <p className="text-sm text-destructive" role="alert">
              {micError}
            </p>
          ) : null}
          <p className="text-xs text-muted-foreground">
            Boardroom and the Interpreter start with this mic. You can still change it for one meeting.
          </p>
          {canCaptureSystemAudio() ? (
            <SettingSwitch
              title="Include call audio"
              detail="Starts on in Boardroom setup: records the other side of a Zoom, Teams or Meet call."
              checked={settings.callAudio}
              onChange={(v) => update({ callAudio: v })}
            />
          ) : null}
        </Section>

        <Section title="During a session">
          <SettingSwitch
            title="Floating widget"
            detail="A small always-on-top timer with pause, notes and agenda when the window is out of sight."
            checked={settings.showWidget}
            onChange={(v) => update({ showWidget: v })}
          />
          <SettingSwitch
            title={mac ? "Shortcut ⌃⌥V" : "Shortcut Ctrl+Alt+V"}
            detail="Brings the session window back from any app."
            checked={settings.shortcut}
            onChange={(v) => update({ shortcut: v })}
          />
        </Section>

        <Section title="App">
          {info?.loginItems !== false ? (
            <SettingSwitch
              title="Open at login"
              detail={mac ? "Starts with your Mac, ready in the menu bar." : "Starts with Windows."}
              checked={settings.openAtLogin}
              onChange={(v) => update({ openAtLogin: v })}
            />
          ) : null}
          <SettingSwitch
            title="Update automatically"
            detail="Downloads in the background and installs when you quit, never during a meeting."
            checked={settings.autoUpdate}
            onChange={(v) => update({ autoUpdate: v })}
          />
          {shell.resetWindowSize ? (
            <div className="flex items-center justify-between gap-3 border-b border-app-text/10 pb-2.5">
              <span className="min-w-0">
                <span className="block text-[15px] font-medium">Window size</span>
                <span className="block text-xs text-muted-foreground">
                  {windowSize ? `Now ${windowSize.width} × ${windowSize.height}. ` : ""}
                  The app remembers it. Default 1280 × 860
                  {mac ? " (Window → Default Size, ⌥⌘0)." : " (Window → Default Size)."}
                </span>
              </span>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="shrink-0 rounded-full"
                disabled={windowSize?.width === 1280 && windowSize?.height === 860}
                onClick={() =>
                  void shell.resetWindowSize?.().then((w) => w && setWindowSize(w))
                }
              >
                Reset
              </Button>
            </div>
          ) : null}
          {info ? (
            <p className="text-xs text-muted-foreground">
              VERBLIZR APP {info.version}
              {info.packaged ? "" : " (development)"}
            </p>
          ) : null}
        </Section>
      </div>
    </main>
  );
}
